import { CloseRounded, ContentCopy, Delete, Download } from '@mui/icons-material'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import { Alert, Avatar, IconButton, Modal, Paper, Skeleton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material'
import { useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import ApiClient from '../../backend/ApiClient'
import { COMPILED_CONSTANTS } from '../../CompiledConstants'
import { ICompiledReport, IReport } from '../../definitions/report_builder/ReportBuilderDefinitions'
import Formatter from '../../helpers/Formatter'
import Helper from '../../helpers/Helper'
import { changeRoute } from '../../store/slices/SessionSlice'
import useCommand from '../hooks/useCommand'
import useInvalidate from '../hooks/useInvalidate'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import Tile from '../layout/Tile'
import ReportPreview from '../reports/ReportPreview'
import { modals } from '../services/ModalService'
import { snackbar } from '../services/SnackbarService'

const Reports = () => {
    const dispatch = useDispatch()
    const invalidate = useInvalidate({ queryKey: buildQueryKey('reports') })

    const [openListReports, setOpenListReports] = useState(false)
    const [reports, setReports] = useState<Array<ICompiledReport & { username: string }>>(null)
    const [pdfUrl, setPdfUrl] = useState<string>(null)
    const [openPreview, setOpenPreview] = useState<boolean>(false)
    const fileName = useRef(null)

    const downloadFromUrl = (url: string, fileName: string) => {
        const a = document.createElement('a')
        a.href = url
        a.download = fileName
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
    }

    const { execute: executeDownloadPdf } = useCommand({
        commandFn: async (fileKey: string) =>
            ApiClient.getPdfPresignedUrl(fileKey, COMPILED_CONSTANTS.evolvereportsBucketName),
        onFinish: async result => {
            if (result.status === 'success') {
                const pdfResponse = await fetch(result.response.url)
                const blob = await pdfResponse.blob()
                const blobUrl = URL.createObjectURL(blob)
                downloadFromUrl(blobUrl, fileName.current)
                URL.revokeObjectURL(blobUrl)
            }
        }
    })

    const { execute: executeGetUrl, status: statusGetUrl } = useCommand({
        commandFn: async (fileKey: string) => await ApiClient.getPdfPresignedUrl(fileKey, COMPILED_CONSTANTS.evolvereportsBucketName),
        onFinish: result => {
            if (result.status === 'success') {
                setPdfUrl(result.response.url)
                setOpenPreview(true)
            }
        }
    })

    const { execute: executeGetReports, status: reportsStatus } = useCommand({
        commandFn: async (templateUUID: string) => {
            const res = await ApiClient.getReportsByTemplate(templateUUID)
            return res
        },
        onFinish: result => {
            if (result.status === 'success') {
                setReports(result.response)
                snackbar.show({ severity: 'success', text: 'Reports fetched successfully' })
            }
            if (result.status === 'error') {
                setOpenListReports(false)
                snackbar.show({ severity: 'error', text: 'Reports fetching went wrong' })
            }
        }
    })

    const { execute: executeDelete } = useCommand({
        commandFn: ({ reportUUID, reportName }: { reportUUID: string, reportName: string }) => modals.openConfirm({
            title: 'Confirm File Deletion',
            startIcon: <Delete />,
            children: <p>Are you sure you want to delete this file?</p>,
            labels: { confirm: 'Delete', cancel: 'Cancel' },
            colors: { confirm: 'error', cancel: 'primary' },
            onConfirm: async () => {
                try {
                    await ApiClient.deleteReport(reportUUID)
                    invalidate()
                    dispatch(changeRoute({ route: 'contracts', params: { tab: 'reports' } }))
                    snackbar.show({ severity: 'success', text: `Report ${reportName} deleted successfully` })
                } catch (error) {
                    snackbar.show({ severity: 'error', text: `Unable to delete report ${reportName}` })
                    console.error(error)
                    return
                }
            }
        })
    })

    const { execute: executeClone } = useCommand({
        commandFn: (report: IReport) => modals.openConfirm({
            title: 'Confirm Cloning',
            startIcon: <ContentCopy />,
            children: <p>Are you sure you want to Clone this file?</p>,
            labels: { confirm: 'Clone', cancel: 'Cancel' },
            colors: { confirm: 'secondary', cancel: 'primary' },
            onConfirm: async () => {
                try {
                    const clonedReport = cloneReport(report)
                    await ApiClient.cloneReport(clonedReport)
                    invalidate()
                    // dispatch(changeRoute({ route: 'contracts', params: { tab: 'reports' } }))
                    snackbar.show({ severity: 'success', text: `Report ${report.name} cloned successfully` })
                } catch (error) {
                    snackbar.show({ severity: 'error', text: `Unable to clone report ${report.name}` })
                    console.error(error)
                    return
                }
            }
        })
    })

    const { response, status } = useQuery<Array<IReport & { username: string }>>({
        queryKey: buildQueryKey('reports'),
        queryFn: () => ApiClient.getReports(),
        cache: true
    })

    const renderLoading = (rows: number) => {
        return (
            <>
                {[...Array(rows)].map((_, index) => (
                    <TableRow key={index}>
                        <TableCell>
                            <Skeleton variant='text' width={120} height={30} />
                        </TableCell>
                        <TableCell>
                            <Stack direction='row' spacing={1} alignItems='center'>
                                <Skeleton variant='circular' width={40} height={40} />
                                <Skeleton variant='text' width={60} />
                            </Stack>
                        </TableCell>
                        <TableCell>
                            <Skeleton variant='text' width={80} />
                        </TableCell>
                        <TableCell>
                            <Skeleton variant='text' width={100} />
                        </TableCell>
                        <TableCell>
                            <Stack direction='row' spacing={1}>
                                <Skeleton variant='circular' width={32} height={32} />
                                <Skeleton variant='circular' width={32} height={32} />
                            </Stack>
                        </TableCell>
                    </TableRow>
                ))}
            </>
        )
    }

    const renderTemplateList = (templates: Array<IReport & { username: string }>) => {
        return (

            <Stack>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <strong>File Name</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Run by</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Run at</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Orientation</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Actions</strong>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {
                                status === 'loading' || status === 'stale'
                                    ? renderLoading(5)
                                    : templates?.map(template => (
                                        <TableRow
                                            hover
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => dispatch(changeRoute({ route: 'report-editor', params: { report: template } }))}
                                            key={template.uuid}>
                                            <TableCell>
                                                {template.name}
                                            </TableCell>
                                            <TableCell>
                                                <Stack
                                                    spacing={1}
                                                    alignItems='center'
                                                    direction='row'>
                                                    <Avatar>{template.username?.charAt(0).toUpperCase()}</Avatar>
                                                    <Typography>
                                                        {template.username}
                                                    </Typography>
                                                </Stack>

                                            </TableCell>

                                            <TableCell>{new Date(template.createdAt).toLocaleDateString()}</TableCell>
                                            <TableCell>{template.pageOrientation}</TableCell>
                                            <TableCell>
                                                <Stack direction='row'>
                                                    <Tooltip title='Clone Report' arrow>
                                                        <IconButton
                                                            onClick={e => {
                                                                e.stopPropagation()
                                                                executeClone(template)
                                                            }}>
                                                            <ContentCopy />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title='List Generated Pdfs' arrow>
                                                        <IconButton
                                                            onClick={e => {
                                                                e.stopPropagation()
                                                                executeGetReports(template.uuid)
                                                                setOpenListReports(true)
                                                            }}>
                                                            <FormatListBulletedIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title='Delete Report' arrow>
                                                        <IconButton
                                                            color='error'
                                                            onClick={e => {
                                                                e.stopPropagation()
                                                                executeDelete({ reportUUID: template.uuid, reportName: template.name })
                                                            }}>
                                                            <Delete />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
            </Stack>
        )
    }

    const renderReportsList = (reports: Array<ICompiledReport & { username: string }>) => {
        return (
            <Stack
                spacing={3}
                overflow='hidden'
                flex={1}>
                <Stack flexDirection='row' justifyContent='space-between'>
                    <Typography variant='h6'>Reports generated using this template</Typography>
                    <IconButton
                        size='small'
                        onClick={() => {
                            setOpenListReports(false)
                        }}>
                        <CloseRounded fontSize='small' />
                    </IconButton>
                </Stack>

                <TableContainer component={Paper}>
                    <Table size='small'>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <strong>File Name</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Run by</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Run at</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Size</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Actions</strong>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {
                                reportsStatus === 'loading' || reportsStatus === 'idle'
                                    ? renderLoading(2)
                                    : (
                                            reports?.length === 0
                                                ? (
                                                        <TableRow>
                                                            <TableCell colSpan={5}>
                                                                <Alert severity='info' sx={{ my: 1 }}>
                                                                    No generated PDFs for this template.
                                                                </Alert>
                                                            </TableCell>
                                                        </TableRow>
                                                    )
                                                : reports?.map(report => (
                                                    <TableRow
                                                        hover
                                                        sx={{ cursor: 'pointer' }}
                                                        onClick={() => {
                                                            executeGetUrl(report.fileKey)
                                                        }}
                                                        key={report.uuid}>
                                                        <TableCell>
                                                            {report.reportName}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Stack
                                                                spacing={1}
                                                                alignItems='center'
                                                                direction='row'>
                                                                <Avatar>{report.username?.charAt(0).toUpperCase()}</Avatar>
                                                                <Typography>
                                                                    {report.username}
                                                                </Typography>
                                                            </Stack>
                                                        </TableCell>
                                                        {/* <TableCell>{new Date(report.createdAt).toLocaleDateString()}</TableCell> */}
                                                        <TableCell>{new Date(report.createdAt).toLocaleString('en-US', {
                                                            year: 'numeric',
                                                            month: '2-digit',
                                                            day: '2-digit',
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                            second: '2-digit'
                                                        })}
                                                        </TableCell>
                                                        <TableCell>{Formatter.bytes(report.fileSize)}</TableCell>
                                                        <TableCell>
                                                            <Stack direction='row'>
                                                                <Tooltip title='Download' arrow>
                                                                    <IconButton
                                                                        onClick={e => {
                                                                            e.stopPropagation()
                                                                            fileName.current = report.reportName
                                                                            executeDownloadPdf(report.fileKey)
                                                                        }}>
                                                                        <Download />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Stack>
                                                        </TableCell>
                                                    </TableRow>
                                                )))
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
            </Stack>
        )
    }

    const cloneReport = (report: IReport): IReport => {
        const clonedReport = { ...report }

        clonedReport.uuid = Helper.generateUUID('report')
        clonedReport.name = `${report.name ?? 'Report Template'} (Clone)`
        const now = new Date().toISOString()
        clonedReport.createdAt = now
        clonedReport.updatedAt = null
        clonedReport._signature = null

        return clonedReport
    }

    return (
        <>
            <Tile.Container style={{ minHeight: '100vh', overflowY: 'auto' }}>
                {renderTemplateList(response)}
            </Tile.Container>

            <Modal
                open={openListReports}
                onClose={() => {
                    setOpenListReports(false)
                    setReports(null)
                }}
                sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Paper
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '40vw',
                        maxHeight: '80vh',
                        padding: 2,
                        overflow: 'hidden' }}>
                    {renderReportsList(reports)}
                </Paper>
            </Modal>

            <Modal
                open={openPreview}
                onClose={() => {
                    setOpenPreview(false)
                }}
                sx={{ margin: 'auto auto', height: '80vh', width: 'calc(90vw - var(--left-nav-width))', left: 'var(--left-nav-width)' }}>
                <ReportPreview
                    onClose={() => {
                        setOpenPreview(false)
                    }}
                    pdfLink={pdfUrl}
                    status={statusGetUrl}
                />
            </Modal>
        </>
    )
}

export default Reports
